import { colors } from 'baseui/tokens';
import { themedStyled } from 'common/styles/styletron.theme';
import { string2money } from 'common/utils/string';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import Text from 'components/elements/text';
import {
  FullContainer,
  HalfContainer,
  RowBetweenStyles,
  SeparatorHorizontal,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import useRecalculate from '../../hooks/use-recalculate';
import { ProductSaleDetailFormType, ProductSaleFormType } from '../form-type';

interface Props {
  onClose: () => void;
}

interface SummaryItemProps {
  productItem: ProductSaleDetailFormType;
}

function SummaryItem(props: SummaryItemProps) {
  const { productItem } = props;
  const price = productItem?.price || 0;
  const qty = productItem?.qty || 0;
  const total = qty * price;

  return (
    <>
      <FullContainer>
        <Text
          variant="ParagraphSmall"
          $style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          ({productItem.qty}x) {productItem.productVariantId.label}
        </Text>
        <SeparatorVertical $margin={8} />
        <FullContainer $style={RowBetweenStyles}>
          <HalfContainer>
            <Text variant="ParagraphSmall" $style={{ color: colors.gray500 }}>
              @ Rp {string2money(price)}
            </Text>
          </HalfContainer>
          <HalfContainer
            $style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Text variant="LabelSmall">Rp {string2money(total)}</Text>
          </HalfContainer>
        </FullContainer>
      </FullContainer>
      <SeparatorVertical $margin={16} />
    </>
  );
}

function Total({ label, price }: { label: string; price: number }) {
  return (
    <TotalContent>
      <TotalItemContent>
        <Text variant="LabelMedium">{label}:</Text>
        <SeparatorHorizontal $margin={8} />
        <Text variant="HeadingXSmall">{`Rp ${string2money(price)}`}</Text>
      </TotalItemContent>
    </TotalContent>
  );
}

export default function SummaryDrawer(props: Props) {
  const { onClose } = props;
  const { t } = useTranslation();

  const { getValues } = useFormContext<ProductSaleFormType>();
  const productSale = getValues(`productSales`);

  const { discount, otherCost, subtotal, total } = useRecalculate();

  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t('product:product_sales_details')}
      />

      <DrawerBody>
        {productSale.map((product) => (
          <SummaryItem
            key={product.productVariantId.id}
            productItem={product}
          />
        ))}
        <div
          style={{
            borderTop: '1px solid #E2E2E2',
          }}
        />
        <Total label={t('common:subtotal')} price={subtotal} />
        <Total label={t('common:discount')} price={discount} />
        <Total label={t('common:other_cost')} price={otherCost} />
        <Total label={t('common:total')} price={total} />
      </DrawerBody>
    </>
  );
}

const TotalContent = themedStyled('div', {
  display: 'flex',
  justifyContent: 'end',
});

const TotalItemContent = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  marginTop: '8px',
  marginBottom: '8px',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: '100%',
  [$theme.mediaQuery.md]: {
    minWidth: '50%',
    maxWidth: '500px',
  },
}));
